import ExpandOutlinedIcon from "@mui/icons-material/ExpandOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import VerticalAlignBottomOutlinedIcon from "@mui/icons-material/VerticalAlignBottomOutlined";
import { Box, ListItemIcon, MenuItem, Stack, styled, Typography } from "@mui/material";
import { FC, useEffect, useRef } from "react";
import { NewRequestField } from "../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-field-infos";
import {
  GetFieldValueCached,
  ResetFieldToDefault,
  SetFieldValue,
  UpdateAllRows,
  UpdateRowsBelow,
  GetFieldIsErroredCached,
} from "../../../Redux/Reducers/redux-slice-new-request";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import MoreMenu, { MoreMenuHandle } from "../../Misc/MoreMenu";
import NewRequestMetadataInput from "../../NewRequest/new-request-metadata-input";
import { useTranslation } from "react-i18next";

export type TableCellSelectProps = {
  // cell
  fieldInfos?: NewRequestField | null;
};

const TableCellNewRequestField: FC<TableCellSelectProps> = (props) => {
  const { t } = useTranslation();
  const { fieldInfos } = props;

  const dispatch = useAppDispatch();
  const moreMenuRef = useRef<MoreMenuHandle>(null);

  const value = useAppSelector((state) =>
    GetFieldValueCached(state, fieldInfos?.columnMetadataFieldId, fieldInfos?.placeholderRowId)
  );
  const isErrored = useAppSelector((state) =>
    GetFieldIsErroredCached(state, fieldInfos?.columnMetadataFieldId, fieldInfos?.placeholderRowId)
  );

  const handleOnChange = (value: any) => {
    if (!fieldInfos) return;
    dispatch(
      SetFieldValue({
        columnMetadataFieldId: fieldInfos.columnMetadataFieldId,
        placeholderRowId: fieldInfos.placeholderRowId,
        value: value,
      })
    );
  };

  return (
    <>
      <StyledStack alignItems="center" direction="row" spacing={0.25} my={0.5} width="100%">
        {(!fieldInfos || !fieldInfos.metadata || !fieldInfos.isApplicable) && (
          <Typography color="text.tier">{"N/A"}</Typography>
        )}
        {fieldInfos && fieldInfos.metadata && fieldInfos.isApplicable && (
          <>
            {!fieldInfos.isValueSettable ? (
              <Typography color="text.primary">{value?.label ?? value}</Typography>
            ) : (
              <>
                <NewRequestMetadataInput
                  metadata={fieldInfos.metadata}
                  value={value}
                  isErrored={isErrored || false}
                  onChange={handleOnChange}
                />
                <Box id="tutorial-request-menu">
                  <MoreMenu
                    ref={moreMenuRef}
                    className={"MoreMenuClass"}
                    direction="vertical"
                    size="small"
                    sx={{ color: "text.secondary" }}
                  >
                    <MenuItem
                      onClick={() => {
                        moreMenuRef.current?.closeMenu();
                        dispatch(
                          UpdateRowsBelow({
                            columnMetadataFieldId: fieldInfos.columnMetadataFieldId,
                            placeholderRowId: fieldInfos.placeholderRowId,
                          })
                        );
                      }}
                      disabled={!value}
                    >
                      <ListItemIcon>
                        <VerticalAlignBottomOutlinedIcon />
                      </ListItemIcon>
                      {t("menu:item.updateRowsBelow")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        moreMenuRef.current?.closeMenu();
                        dispatch(
                          UpdateAllRows({
                            columnMetadataFieldId: fieldInfos.columnMetadataFieldId,
                            placeholderRowId: fieldInfos.placeholderRowId,
                          })
                        );
                      }}
                      disabled={!value}
                    >
                      <ListItemIcon>
                        <ExpandOutlinedIcon />
                      </ListItemIcon>
                      {t("menu:item.updateAllTheColumn")}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        moreMenuRef.current?.closeMenu();
                        dispatch(
                          ResetFieldToDefault({
                            columnMetadataFieldId: fieldInfos.columnMetadataFieldId,
                            placeholderRowId: fieldInfos.placeholderRowId,
                          })
                        );
                      }}
                    >
                      <ListItemIcon>
                        <UpdateOutlinedIcon />
                      </ListItemIcon>
                      {t("panel:newRequestFiltersPanel.resetToDefaultLink")}
                    </MenuItem>
                  </MoreMenu>
                </Box>
              </>
            )}
          </>
        )}
      </StyledStack>
    </>
  );
};

export default TableCellNewRequestField;

const StyledStack = styled(Stack)(({ theme }) => ({
  maxWidth: "100%",
  ".MoreMenuClass": {
    opacity: 0,
    transition: "opacity .3s",
  },
  "&:hover": {
    ".MoreMenuClass": {
      opacity: 1,
    },
  },
}));
